import React from 'react'
import { useTheme } from 'src/context/ThemeContext'

export interface MerchantProductCardProps {
  id: string
  name: string
  sku: string
  brand: string
  image?: string
  price: {
    current: number
    range?: { min: number; max: number }
    currency: string
  }
  stock: {
    quantity: number
    status: "in-stock" | "low-stock" | "out-of-stock"
  }
  sales: {
    quantity30d: number
    revenue30d: number
  }
  variants?: {
    count: number
    priceRange?: { min: number; max: number }
  }
  quality: "good" | "incomplete" | "poor"
  onAction: (action: string, productId: string) => void
  className?: string
}

function MerchantProductCard({
  id,
  name,
  sku,
  brand,
  image,
  price,
  stock,
  sales,
  variants,
  quality,
  onAction,
  className = "",
}: MerchantProductCardProps) {
  const { theme } = useTheme()

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const formatCurrency = (amount: number) => {
    return `Rp ${amount.toLocaleString()}`
  }

  const getStockStatusClass = () => {
    switch (stock.status) {
      case "out-of-stock":
        return "tw-text-red-600 tw-bg-red-50 dark:tw-text-red-400 dark:tw-bg-red-900/50";
      case "low-stock":
        return "tw-text-orange-600 tw-bg-orange-50 dark:tw-text-orange-400 dark:tw-bg-orange-900/50";
      default:
        return "tw-text-green-600 tw-bg-green-50 dark:tw-text-green-400 dark:tw-bg-green-900/50";
    }
  };

  const getQualityBadgeClass = () => {
    switch (quality) {
      case "good":
        return "tw-bg-green-100 dark:tw-bg-green-900/50 tw-text-green-700 dark:tw-text-green-300"
      case "incomplete":
        return "tw-bg-orange-100 dark:tw-bg-orange-900/50 tw-text-orange-700 dark:tw-text-orange-300"
      case "poor":
        return "tw-bg-red-100 dark:tw-bg-red-900/50 tw-text-red-700 dark:tw-text-red-300"
      default:
        return "tw-bg-gray-100 dark:tw-bg-gray-900/50 tw-text-gray-700 dark:tw-text-gray-300"
    }
  }

  const getPriceDisplay = () => {
    if (variants?.priceRange) {
      return `${formatCurrency(variants.priceRange.min)} - ${formatCurrency(variants.priceRange.max)}`
    }
    return formatCurrency(price.current)
  }

  return (
    <div
      className={`tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-background-light dark:tw-bg-slate-800/60 tw-shadow-sm tw-relative tw-border tw-border-slate-200/60 dark:tw-border-slate-700/60 ${className}`}
    >
      {/* Card Content */}
      <div className="tw-flex tw-flex-col tw-p-3">
        <div className="tw-flex tw-gap-3">
          {/* Product Image */}
          <div
            className="tw-bg-center tw-bg-no-repeat tw-aspect-square tw-bg-cover tw-rounded-lg tw-h-10 tw-w-10 tw-shrink-0 tw-shadow-sm"
            style={{ 
              backgroundImage: image ? `url(${image})` : 'none',
              backgroundColor: !image ? '#f3f4f6' : 'transparent'
            }}
            data-alt={`Image of ${name}`}
          >
            {!image && (
              <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full">
                <span className="material-symbols-outlined tw-text-gray-400 tw-text-lg">
                  image
                </span>
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-justify-start">
            <p className={`tw-text-base tw-font-semibold tw-line-clamp-1 tw-leading-tight ${
              theme === 'light' ? 'tw-text-slate-900' : 'tw-text-white'
            }`}>
              {name}
            </p>
            
            <div className="tw-mt-1 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
              <span className="tw-font-medium">{brand}</span>
              <span>•</span>
              <span>{sku}</span>
              {variants && (
                <>
                  <span>•</span>
                  <span className="tw-text-primary tw-font-medium">{variants.count} variants</span>
                </>
              )}
            </div>

            <div className="tw-mt-2 tw-flex tw-items-baseline tw-gap-3">
              <span className={`tw-text-xl tw-font-bold ${
                theme === 'light' ? 'tw-text-slate-900' : 'tw-text-white'
              }`}>
                {getPriceDisplay()}
              </span>
              <div className="tw-flex tw-items-baseline tw-gap-1 tw-text-slate-500 dark:tw-text-slate-400">
                <span className="tw-text-sm">Stock:</span>
                <span
                  className={`tw-text-lg tw-font-bold ${
                    stock.quantity === 0
                      ? "tw-text-red-500 dark:tw-text-red-400"
                      : theme === 'light' ? "tw-text-slate-900" : "tw-text-white"
                  }`}
                >
                  {stock.quantity}
                </span>
              </div>
            </div>

            <div className="tw-flex tw-items-center tw-justify-between tw-pt-3 tw-mt-1 tw-border-t tw-border-slate-100 dark:tw-border-slate-700/50">
              {/* Sales Metrics */}
              <div className="tw-flex tw-items-center tw-gap-3 tw-text-slate-500 dark:tw-text-slate-400">
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span className="material-symbols-outlined tw-text-sm">
                    trending_up
                  </span>
                  <span className="tw-text-xs tw-font-medium">
                    {formatNumber(sales.quantity30d)} sold
                  </span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span className="material-symbols-outlined tw-text-sm">
                    payments
                  </span>
                  <span className="tw-text-xs tw-font-medium">
                    {formatCurrency(sales.revenue30d)}
                  </span>
                </div>
              </div>

              <span
                className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium ${getQualityBadgeClass()}`}
              >
                {quality === "good" ? (
                  <span className="tw-size-1.5 tw-rounded-full tw-bg-green-500"></span>
                ) : quality === "incomplete" ? (
                  <span className="material-symbols-outlined tw-text-xs !tw-font-thin">
                    warning
                  </span>
                ) : (
                  <span className="tw-size-1.5 tw-rounded-full tw-bg-red-500"></span>
                )}
                {quality === "good"
                  ? "Good"
                  : quality === "incomplete"
                  ? "Incomplete"
                  : "Poor"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-border-t tw-border-slate-200 dark:tw-border-slate-700 tw-p-2 tw-bg-slate-50/50 dark:tw-bg-slate-800/30">
        <button
          className="tw-flex tw-h-9 tw-flex-1 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 dark:tw-bg-primary/20 tw-px-2 tw-text-primary tw-transition-all tw-duration-200 hover:tw-bg-primary/20 hover:tw-shadow-sm active:tw-bg-primary/30"
          onClick={(e) => {
            e.stopPropagation();
            onAction("update-stock", id);
          }}
        >
          <span className="tw-text-xs tw-font-semibold tw-leading-tight">
            Update Stock
          </span>
        </button>
        <button
          className="tw-flex tw-h-9 tw-flex-1 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 dark:tw-bg-primary/20 tw-px-2 tw-text-primary tw-transition-all tw-duration-200 hover:tw-bg-primary/20 hover:tw-shadow-sm active:tw-bg-primary/30"
          onClick={(e) => {
            e.stopPropagation();
            onAction("update-price", id);
          }}
        >
          <span className="tw-text-xs tw-font-semibold tw-leading-tight">
            Update Price
          </span>
        </button>
        <button
          className="tw-flex tw-h-9 tw-flex-1 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 dark:tw-bg-primary/20 tw-px-2 tw-text-primary tw-transition-all tw-duration-200 hover:tw-bg-primary/20 hover:tw-shadow-sm active:tw-bg-primary/30"
          onClick={(e) => {
            e.stopPropagation();
            onAction("view-details", id);
          }}
        >
          <span className="tw-text-xs tw-font-semibold tw-leading-tight">
            View Details
          </span>
        </button>
      </div>
    </div>
  )
}

export default MerchantProductCard